import React, { FormEvent, useEffect, useRef, useState } from 'react'
import { router } from '@inertiajs/react'
import { useReturnTo } from '@/contexts/ReturnToContext'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import Chip from '@mui/material/Chip'
import Slide from '@mui/material/Slide'
import Collapse from '@mui/material/Collapse'
import CloseIcon from '@mui/icons-material/Close'
import MinimizeIcon from '@mui/icons-material/Minimize'
import OpenInFullIcon from '@mui/icons-material/OpenInFull'
import AttachFileIcon from '@mui/icons-material/AttachFile'
import RateReviewIcon from '@mui/icons-material/RateReview'

interface AgentRef {
  id: number
  username: string
  slug: string
}

interface Props {
  open: boolean
  onClose: () => void
  boardSlug: string
  taskId: number
  defaultAssigneeId: number
  agents: AgentRef[]
}

export function ReviewComposerPanel({ open, onClose, boardSlug, taskId, defaultAssigneeId, agents }: Props) {
  const returnTo = useReturnTo()
  const [minimized, setMinimized] = useState(false)
  const [message, setMessage] = useState('')
  const [assigneeId, setAssigneeId] = useState<number | ''>(defaultAssigneeId)
  const [files, setFiles] = useState<File[]>([])
  const [processing, setProcessing] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (open) {
      setMessage('')
      setAssigneeId(defaultAssigneeId)
      setFiles([])
      setMinimized(false)
    }
  }, [open, defaultAssigneeId])

  function handleFilesSelected(e: React.ChangeEvent<HTMLInputElement>) {
    const selected = e.target.files
    if (!selected || selected.length === 0) return
    setFiles(prev => [...prev, ...Array.from(selected)])
    if (fileRef.current) fileRef.current.value = ''
  }

  function removeFile(index: number) {
    setFiles(prev => prev.filter((_, i) => i !== index))
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    if (!message.trim()) return

    const formData = new FormData()
    formData.append('review_item[message]', message)
    formData.append('review_item[assignee_id]', String(assigneeId))
    files.forEach(file => {
      formData.append('attachments[][file]', file)
    })
    formData.append('return_to', returnTo || window.location.pathname)

    setProcessing(true)
    router.post(
      `/tasks/boards/${boardSlug}/tasks/${taskId}/review_items`,
      formData as any,
      {
        forceFormData: true,
        onSuccess: () => onClose(),
        onFinish: () => setProcessing(false),
      }
    )
  }

  return (
    <Slide direction="up" in={open} mountOnEnter unmountOnExit>
      <Paper
        elevation={8}
        sx={{
          position: 'fixed',
          bottom: 0,
          right: 24,
          width: 420,
          maxWidth: 'calc(100vw - 48px)',
          zIndex: 1200,
          borderRadius: '8px 8px 0 0',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header bar */}
        <Box
          onClick={() => setMinimized(prev => !prev)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 2,
            py: 1,
            backgroundColor: 'primary.main',
            color: 'primary.contrastText',
            cursor: 'pointer',
            userSelect: 'none',
          }}
        >
          <RateReviewIcon sx={{ fontSize: 18 }} />
          <Typography variant="body2" fontWeight={600} sx={{ flex: 1 }}>
            New Review Item
          </Typography>
          <IconButton
            size="small"
            onClick={(e) => { e.stopPropagation(); setMinimized(prev => !prev) }}
            sx={{ color: 'inherit', p: 0.5 }}
          >
            {minimized ? <OpenInFullIcon sx={{ fontSize: 16 }} /> : <MinimizeIcon sx={{ fontSize: 16 }} />}
          </IconButton>
          <IconButton
            size="small"
            onClick={(e) => { e.stopPropagation(); onClose() }}
            sx={{ color: 'inherit', p: 0.5 }}
          >
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>

        {/* Collapsible body */}
        <Collapse in={!minimized}>
          <Box
            component="form"
            onSubmit={handleSubmit}
            noValidate
            sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}
          >
            <TextField
              label="Describe the review item"
              fullWidth
              autoFocus
              multiline
              rows={4}
              value={message}
              onChange={e => setMessage(e.target.value)}
              size="small"
            />

            <FormControl fullWidth size="small">
              <InputLabel>Assign to</InputLabel>
              <Select
                value={assigneeId}
                label="Assign to"
                onChange={e => setAssigneeId(e.target.value as number)}
              >
                {agents.map((a) => (
                  <MenuItem key={a.id} value={a.id}>{a.username}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box>
              <input
                ref={fileRef}
                type="file"
                multiple
                hidden
                onChange={handleFilesSelected}
              />
              <Button
                size="small"
                variant="outlined"
                startIcon={<AttachFileIcon />}
                onClick={() => fileRef.current?.click()}
                sx={{ textTransform: 'none' }}
              >
                Attach files
              </Button>
              {files.length > 0 && (
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 1 }}>
                  {files.map((file, i) => (
                    <Chip
                      key={`${file.name}-${i}`}
                      label={file.name}
                      size="small"
                      onDelete={() => removeFile(i)}
                      variant="outlined"
                    />
                  ))}
                </Box>
              )}
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
              <Button size="small" onClick={onClose}>
                Cancel
              </Button>
              <Button
                size="small"
                type="submit"
                variant="contained"
                disabled={processing || !message.trim()}
              >
                Add Review
              </Button>
            </Box>
          </Box>
        </Collapse>
      </Paper>
    </Slide>
  )
}
